/**
 * Persona-related utility functions
 */

import { stripEmojis } from './textUtils';

/**
 * Gender options for persona creation
 */
export const GENDER_OPTIONS = ["Male", "Female", "Non Binary"];

/**
 * Mapping from INTERESTS emoji to translation key
 */
export const INTERESTS_KEY_MAP = {
    "🎨 Art and Crafting": "art",
    "🎮 Gaming": "gaming",
    "📚 Reading": "reading",
    "🎵 Music": "music",
    "🎥 Movies and TV Shows": "movies",
    "🏋️‍♂️ Fitness and Exercise": "fitness",
    "🍳 Cooking and Baking": "cooking",
    "🌍 Traveling": "traveling",
    "📸 Photography": "photography",
    "🌱 Gardening": "gardening",
    "🐾 Pets and Animal Care": "pets",
    "🚴‍♂️ Outdoor Activities": "outdoor",
    "🧩 Puzzles and Board Games": "puzzles",
    "🛠️ DIY Projects": "diy",
    "✍️ Writing or Journaling": "writing",
    "🧘‍♀️ Meditation and Yoga": "meditation",
    "⚽ Sports": "sports",
    "💻 Technology and Gadgets": "tech",
    "🏖️ Beach and Water Activities": "beach"
};

/**
 * Trim extra spaces from a string
 * @param {string} str - String to trim
 * @returns {string} Trimmed string
 */
export const trimSpaces = (str) => {
    if (typeof str !== 'string') return str;
    return str.replace(/\s+/g, ' ').trim();
};

/**
 * Map array items to emoji format (for display)
 * @param {Array} items - Array of items
 * @returns {Array} Items as-is (display uses emoji versions from INTERESTS/PERSONALITY_TRAITS)
 */
export const mapToEmoji = (items) => items || [];

/**
 * Map array items from emoji to plain format (for API)
 * Uses stripEmojis to remove emojis
 * @param {Array} items - Array of items with emojis
 * @returns {Array} Plain text items
 */
export const mapToPlain = (items) => (items || []).map(item => stripEmojis(trimSpaces(item)));

/**
 * Map plain text items to emoji format (for display)
 * @param {Array} items - Array of plain text items
 * @param {Array} emojiOptions - Array of options with emojis
 * @returns {Array} Items with emojis if matched
 */
export const mapToEmojiFormat = (items, emojiOptions) => {
    if (!items || !emojiOptions) return [];
    
    return items.map(item => {
        const plainText = stripEmojis(trimSpaces(item));
        const matched = emojiOptions.find(opt => stripEmojis(trimSpaces(opt)) === plainText);
        return matched || item;
    });
};

/**
 * Get interest options with translations
 * @param {Function} t - Translation function
 * @returns {Array} Options array with value and label
 */
export const getInterestOptions = (t) => {
    return INTERESTS.map(interest => {
        const key = INTERESTS_KEY_MAP[interest];
        return { value: interest, label: t(`interestsList.${key}`, { defaultValue: interest }) };
    });
};

/**
 * Get personality options with translations
 * @param {Function} t - Translation function
 * @returns {Array} Options array with value and label
 */
export const getPersonalityOptions = (t) => {
    return PERSONALITY_TRAITS.map(trait => {
        const key = trait.split(" ")[1]?.toLowerCase();
        return { value: trait, label: t(`personalityTraits.${key}`, { defaultValue: trait }) };
    });
};

/**
 * Get interest options from API response values (handles unknown values)
 * @param {Array} apiValues - Array of interest values from API (plain text)
 * @param {Function} t - Translation function
 * @returns {Array} Options array with value and label
 */
export const getInterestOptionsFromApi = (apiValues, t) => {
    const knownOptions = getInterestOptions(t);
    const knownValues = new Set(knownOptions.map(o => stripEmojis(trimSpaces(o.value))));
    
    const options = [...knownOptions];
    
    (apiValues || []).forEach(val => {
        const plainVal = stripEmojis(trimSpaces(val));
        if (!knownValues.has(plainVal)) {
            options.push({ value: val, label: val });
            knownValues.add(plainVal);
        }
    });
    
    return options;
};

/**
 * Get personality options from API response values (handles unknown values)
 * @param {Array} apiValues - Array of personality values from API (plain text)
 * @param {Function} t - Translation function
 * @returns {Array} Options array with value and label
 */
export const getPersonalityOptionsFromApi = (apiValues, t) => {
    const knownOptions = getPersonalityOptions(t);
    const knownValues = new Set(knownOptions.map(o => stripEmojis(trimSpaces(o.value))));
    
    const options = [...knownOptions];
    
    (apiValues || []).forEach(val => {
        const plainVal = stripEmojis(trimSpaces(val));
        if (!knownValues.has(plainVal)) {
            options.push({ value: val, label: val });
            knownValues.add(plainVal);
        }
    });
    
    return options;
};

/**
 * Interests and hobbies options
 */
export const INTERESTS = [
  "🎨 Art and Crafting",
  "🎮 Gaming",
  "📚 Reading",
  "🎵 Music",
  "🎥 Movies and TV Shows",
  "🏋️‍♂️ Fitness and Exercise",
  "🍳 Cooking and Baking",
  "🌍 Traveling",
  "📸 Photography",
  "🌱 Gardening",
  "🐾 Pets and Animal Care",
  "🚴‍♂️ Outdoor Activities",
  "🧩 Puzzles and Board Games",
  "🛠️ DIY Projects",
  "✍️ Writing or Journaling",
  "🧘‍♀️ Meditation and Yoga",
  "⚽ Sports",
  "💻 Technology and Gadgets",
  "🏖️ Beach and Water Activities",
];

/**
 * Personality traits options
 */
export const PERSONALITY_TRAITS = [
  "😄 Fun Loving",
  "🤔 Thoughtful",
  "🎯 Goal Oriented",
  "💡 Creative",
  "📖 Analytical",
  "🌟 Inspirational",
  "🏃 Energetic",
  "🧘 Calm and Zen",
  "💪 Adventurous",
  "🤝 Supportive",
  "🎭 Dramatic",
  "😊 Cheerful",
  "😌 Peaceful",
  "🔥 Passionate",
  "💪 Driven",
  "🌈 Optimistic",
  "🔍 Curious",
  "⭐ Romantic",
  "🎤 Outgoing",
  "📚 Introvert",
];

/**
 * Validate persona form data
 * @param {Object} formData - The form data to validate
 * @param {string} formData.name - Persona name
 * @param {string} formData.age - Persona age
 * @param {string} formData.gender - Persona gender
 * @returns {Object} Object containing validation errors (empty if valid)
 */
export const validatePersonaForm = (formData) => {
  const errors = {};

  if (!formData.name?.trim()) {
    errors.name = "Name is required";
  }

  if (!formData.age?.trim()) {
    errors.age = "Age is required";
  }

  if (!formData.gender) {
    errors.gender = "Gender is required";
  }

  return errors;
};

/**
 * Toggle an item in a list (add if not present, remove if present)
 * @param {*} item - The item to toggle
 * @param {Array} list - The current list
 * @returns {Array} The updated list
 */
export const toggleItemInList = (item, list) => {
  if (list.includes(item)) {
    return list.filter((i) => i !== item);
  }
  return [...list, item];
};

/**
 * Convert a file to base64 data URL
 * @param {File} file - The file to convert
 * @returns {Promise<string>} Promise that resolves to the base64 data URL
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user has a token in localStorage
 */
export const isUserAuthenticated = () => {
  return !!window.localStorage.getItem("token");
};

/**
 * Get the first character of a name in uppercase
 * @param {string} name - The name to get initial from
 * @returns {string} The first character in uppercase or "?"
 */
export const getPersonaInitial = (name) => {
  return name ? name.charAt(0).toUpperCase() : "?";
};

/**
 * Convert curly brace format to HTML mention tags
 * Handles both regular mentions {text} and edited mentions {{{text}}}
 * @param {string} text - The text containing curly brace mentions
 * @returns {string} HTML string with mention spans
 */
export const convertCurlyBracesToMentionTags = (text) => {
  if (!text) return "";
  
  // Handle edited mentions (triple curly braces)
  let formattedText = text.replace(
    /\{\{\{([^}]+)\}\}\}/g,
    (_, p1) =>
      `<span data-type="mention" class="mention edited" data-id="1" data-label="${p1}" isedited="true"><i>${p1}</i></span>`
  );

  // Handle regular mentions (single curly braces)
  formattedText = formattedText.replace(
    /\{([^}]+)\}/g,
    (_, p1) =>
      `<span data-type="mention" class="mention" data-id="1" data-label="${p1}"><i>${p1}</i></span>`
  );
  
  return formattedText;
};
